/* STEP 11b Part 2 · F2 — "Figures the trial balance does not hold" on the statement of cash flows of a TRIAL-BALANCE year
   (cash_flow_tb.py). A trial balance has no vouchers: AZEX derives what it can (FVOCI fair-value change, EOSB remeasurement,
   associates' share of profit, receivables written off — listed here) and asks for what it cannot hold: the dividends paid in
   the year and the proceeds / cost of PPE disposed of. When the closing cash does not tie and one of them is missing, it is
   named in red (never a closing cash that does not tie without saying why). Saved with PUT
   /engagements/{id}/cash-flow-tb-inputs; the statement and Validation are read again. IFRS.jsx is near 1,500 lines (the
   repository's file-size rule): this is its own file. EN + AR.
   STEP 11c · 2.4 (owner decision D1): when the trial balance shows a movement on the associate investment, the dividends
   received from associates (and any purchase / disposal of the investment) are asked here too, the same way as the dividends
   paid — never guessed from the net movement (``tb_inputs.assoc_asked``).
   STEP 11d · A3: once a figure is entered, a decrease of the investment the share of profit and the figures entered do not
   explain is asked as "Proceeds from disposal of associates" (``needed`` "assoc_disposal", the gap named by the server).
   STEP 11d-7 · item 3: the same for an increase — asked as "Purchase of investment in associates" (``needed``
   "assoc_purchase"); entered, it is the cash flow's own line "Investment in associates and joint ventures". */
import React, { useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtNumber } from "@/components/Amount";
import { asArray } from "@/lib/asArray";
import { toast } from "sonner";

const FIELDS = [["dividends_paid", "dividends_paid"], ["ppe_disposal_proceeds", "ppe_disposal"], ["ppe_disposal_cost", "ppe_disposal"]];
const ASSOC_FIELDS = [["assoc_dividends_received", "assoc"], ["assoc_purchases", "assoc"], ["assoc_disposal_proceeds", "assoc"]];
const ALL_FIELDS = [...FIELDS, ...ASSOC_FIELDS];
// STEP 11d · A3: the field that goes red for each "needed" group (the disposal proceeds also for "assoc_disposal")
const isNeeded = (needed, k, group) => needed.includes(group) || (k === "assoc_disposal_proceeds" && needed.includes("assoc_disposal"))
  || (k === "assoc_purchases" && needed.includes("assoc_purchase"));    // STEP 11d-7 · item 3
const labelKey = (k) => (k.startsWith("assoc_") ? `s11c2b.assoc.${k}` : `s11b2.f2.${k}`);
const DERIVED = ["fvoci_oci", "eosb_oci_tax", "assoc_share", "receivables_written_off"];

export default function IFRSCashFlowTbInputs({ cf, engagementId, onChanged }) {
  const { t, lang } = useI18n();
  const info = cf?.tb_inputs;
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const v = info?.values || {};
    setVals(Object.fromEntries(ALL_FIELDS.map(([k]) => [k, v[k] == null ? "" : String(v[k])])));
  }, [info]);   // eslint-disable-line react-hooks/exhaustive-deps -- reset when the server's figures change
  if (!info?.tb_year) return null;
  const needed = asArray(info.needed);
  const derived = info.derived || {};
  const shown = info.assoc_asked ? ALL_FIELDS : FIELDS;     // STEP 11c · 2.4: the associate figures only when the TB asks for them
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(shown.map(([k]) => [k, vals[k] === "" ? null : Number(vals[k])]));
      await api.put(`/engagements/${engagementId}/cash-flow-tb-inputs`, body);
      toast.success(t("s11b2.f2.saved"));
      const [c, v] = await Promise.all([api.get(`/fs/${engagementId}/cash-flow`), api.get(`/fs/${engagementId}/validation`)]);
      onChanged?.(c.data, v.data);
    } catch (e) { toast.error(toErrMsg(e, t("common.failed"))); } finally { setBusy(false); }
  };
  return (
    <div className="border-b bg-amber-50/60 px-4 py-2 text-xs dark:bg-amber-950/20" data-testid="cf-tb-inputs">
      <div className="font-semibold">{t("s11b2.f2.title")}</div>
      <div className="mb-1 text-[11px] text-muted-foreground">{t("s11b2.f2.hint")}</div>
      {needed.length > 0 && (
        <div className="mb-1 text-red-700" data-testid="cf-tb-inputs-needed">
          {lang === "ar" ? cf.tb_inputs_needed_text_ar : cf.tb_inputs_needed_text}</div>)}
      {info.assoc_asked && <div className="mb-1 text-[11px] text-muted-foreground" data-testid="cf-tb-assoc-hint">{t("s11c2b.assoc.hint")}</div>}
      {needed.includes("assoc_disposal") && (
        <div className="mb-1 text-[11px] text-red-700" data-testid="cf-tb-assoc-disposal-hint">{t("s11d1b.assoc.disposalHint")}</div>)}
      {needed.includes("assoc_purchase") && (
        <div className="mb-1 text-[11px] text-red-700" data-testid="cf-tb-assoc-purchase-hint">{t("s11d7b.assoc.purchaseHint")}</div>)}
      <div className="flex flex-wrap items-end gap-3">
        {shown.map(([k, group]) => (
          <label key={k} className={`flex flex-col gap-0.5 ${isNeeded(needed, k, group) ? "text-red-700" : ""}`}
                 data-needed={isNeeded(needed, k, group) ? "1" : "0"} data-testid={`cf-tb-field-${k}`}>
            <span>{t(labelKey(k))}</span>
            <Input type="number" step="0.01" min="0" className="h-8 w-44 text-end font-mono" value={vals[k] ?? ""}
                   onChange={(e) => setVals((s) => ({ ...s, [k]: e.target.value }))} data-testid={`cf-tb-input-${k}`} />
          </label>
        ))}
        <Button size="sm" onClick={save} disabled={busy || !engagementId} data-testid="cf-tb-inputs-save">{t("s11b2.f2.save")}</Button>
      </div>
      {DERIVED.some((k) => derived[k] != null) && (
        <div className="mt-1 text-[11px] text-muted-foreground" data-testid="cf-tb-derived">
          {t("s11b2.f2.derivedTitle")}{" "}
          {DERIVED.filter((k) => derived[k] != null).map((k) => `${t(`s11b2.f2.derived.${k}`)} ${fmtNumber(derived[k])}`)
            .join(lang === "ar" ? "؛ " : "; ")}
        </div>)}
    </div>
  );
}
